import type { Locale } from './LanguageContext';

/**
 * Locale-aware number/date/currency formatting via the browser's built-in
 * `Intl` APIs - see apps/ops/src/i18n/format.ts's own comment for the
 * reasoning (identical file, separate copy per app).
 */

const INTL_LOCALE: Record<Locale, string> = { en: 'en-US', ru: 'ru-RU' };

export function formatDate(value: string | Date, locale: Locale, opts?: Intl.DateTimeFormatOptions): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  return new Intl.DateTimeFormat(INTL_LOCALE[locale], opts ?? { dateStyle: 'medium' }).format(date);
}

export function formatDateTime(value: string | Date, locale: Locale): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  return new Intl.DateTimeFormat(INTL_LOCALE[locale], { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

export function formatNumber(value: number | string, locale: Locale, opts?: Intl.NumberFormatOptions): string {
  const n = typeof value === 'string' ? Number(value) : value;
  return new Intl.NumberFormat(INTL_LOCALE[locale], opts).format(n);
}

/**
 * `amount CODE` (e.g. "199.00 EUR" / "199,00 EUR"), never a currency symbol
 * or locale-flipped ordering - see apps/ops/src/i18n/format.ts's own
 * comment for the reasoning (identical file, separate copy per app).
 */
export function formatCurrencyMinor(amountMinor: number | string, currency: string, locale: Locale): string {
  const n = (typeof amountMinor === 'string' ? Number(amountMinor) : amountMinor) / 100;
  const formatted = new Intl.NumberFormat(INTL_LOCALE[locale], { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
  return `${formatted} ${currency}`;
}

/** Intl.PluralRules category for `n` in `locale` - 'one'|'few'|'many'|'other' (ru has all four; en only 'one'/'other'). */
export function pluralCategory(n: number, locale: Locale): Intl.LDMLPluralRule {
  return new Intl.PluralRules(INTL_LOCALE[locale]).select(n);
}
